// Claude API (api.anthropic.com /v1/messages) のRESTアダプタ。
// ブラウザから直接呼ぶため anthropic-dangerous-direct-browser-access ヘッダーを付与する
// （このツールはAPIキーをlocalStorageにのみ保存するローカルツールであり、キーは利用者自身のもの）。

import {
  postJsonWithRetry,
  retryAfterMs,
  type ChatContent,
  type ChatPart,
  type GenerateRequest,
  type GenerateResponse,
  type ThinkingConfig,
} from './llm.js'

const API_URL = 'https://api.anthropic.com/v1/messages'
const ANTHROPIC_VERSION = '2023-06-01'
// 非ストリーミングでタイムアウトしない範囲の上限
const MAX_OUTPUT_TOKENS = 16000

const EFFORT_LEVELS = new Set(['high', 'medium', 'low', 'xhigh', 'max'])

interface AnthropicMessage {
  role: 'user' | 'assistant'
  content: Record<string, unknown>[]
}

/**
 * 思考指定を Claude の adaptive thinking + effort に変換する。
 * - high/medium/low/xhigh/max → adaptive + output_config.effort
 * - minimal → adaptive + effort "low"
 * - none / budget 0 → 思考オフ（パラメータ省略。Opus 4.8系は省略時思考なし）
 * - budget その他（-1等の「思考オン」） → adaptive のみ
 */
function thinkingParams(config?: ThinkingConfig): Record<string, unknown> {
  if (!config) return {}
  const level = config.thinkingLevel
  if (level) {
    if (EFFORT_LEVELS.has(level)) {
      return { thinking: { type: 'adaptive' }, output_config: { effort: level } }
    }
    if (level === 'minimal') {
      return { thinking: { type: 'adaptive' }, output_config: { effort: 'low' } }
    }
    return {} // 'none' など → 思考オフ
  }
  if (config.thinkingBudget !== undefined && config.thinkingBudget !== 0) {
    return { thinking: { type: 'adaptive' } }
  }
  return {}
}

/**
 * 中立フォーマットの履歴を Claude の messages 形式に変換する。
 * - Claude産の思考ブロックは署名ごと復元して返す（ツールループ継続に必要）。
 *   他プロバイダ産の思考は送らない
 * - Gemini産の履歴には functionCall の id が無いため、呼び出しと応答の順序対応を
 *   保ったまま合成IDで補う
 * - Claude は user/assistant の交互を要求するため、連続する同一ロールは1メッセージに結合する
 */
function toAnthropicMessages(contents: ChatContent[]): AnthropicMessage[] {
  const messages: AnthropicMessage[] = []
  let syntheticIdCounter = 0
  // 直前のassistantメッセージで id を合成した functionCall のID列（応答側で同順に消費）
  let pendingSyntheticIds: string[] = []

  for (const content of contents) {
    const role = content.role === 'model' ? 'assistant' : 'user'
    const blocks: Record<string, unknown>[] = []
    if (role === 'assistant') pendingSyntheticIds = []

    for (const part of content.parts) {
      if (part.thought || part.redactedThinking !== undefined) {
        if (part.thoughtProvider !== 'anthropic') continue
        if (part.redactedThinking !== undefined) {
          blocks.push({ type: 'redacted_thinking', data: part.redactedThinking })
        } else {
          blocks.push({
            type: 'thinking',
            thinking: part.text ?? '',
            signature: part.thoughtSignature ?? '',
          })
        }
        continue
      }
      if (part.functionCall) {
        let id = part.functionCall.id
        if (!id) {
          id = `legacy_call_${syntheticIdCounter++}`
          pendingSyntheticIds.push(id)
        }
        blocks.push({
          type: 'tool_use',
          id,
          name: part.functionCall.name,
          input: part.functionCall.args,
        })
        continue
      }
      if (part.functionResponse) {
        const response = part.functionResponse.response
        const isError = 'error' in response
        const message = response['result'] ?? response['error']
        blocks.push({
          type: 'tool_result',
          tool_use_id: part.functionResponse.id ?? pendingSyntheticIds.shift() ?? '',
          content: typeof message === 'string' ? message : JSON.stringify(response),
          ...(isError ? { is_error: true } : {}),
        })
        continue
      }
      if (part.text !== undefined && part.text !== '') {
        blocks.push({ type: 'text', text: part.text })
      }
    }

    if (blocks.length === 0) continue
    const last = messages.at(-1)
    if (last && last.role === role) {
      last.content.push(...blocks)
    } else {
      messages.push({ role, content: blocks })
    }
  }

  return messages
}

interface AnthropicResponse {
  content?: Array<{
    type: string
    text?: string
    thinking?: string
    signature?: string
    data?: string
    id?: string
    name?: string
    input?: Record<string, unknown>
  }>
  stop_reason?: string
}

function toGenerateResponse(json: AnthropicResponse): GenerateResponse {
  const parts: ChatPart[] = []

  for (const block of json.content ?? []) {
    switch (block.type) {
      case 'text':
        if (block.text) parts.push({ text: block.text })
        break
      case 'thinking':
        parts.push({
          thought: true,
          thoughtProvider: 'anthropic',
          text: block.thinking ?? '',
          ...(block.signature ? { thoughtSignature: block.signature } : {}),
        })
        break
      case 'redacted_thinking':
        parts.push({
          thought: true,
          thoughtProvider: 'anthropic',
          redactedThinking: block.data ?? '',
        })
        break
      case 'tool_use':
        parts.push({
          functionCall: {
            name: block.name ?? '',
            args: block.input ?? {},
            ...(block.id ? { id: block.id } : {}),
          },
        })
        break
    }
  }

  // 安全システムによる拒否（中身が無い場合はブロックとして扱い、ユーザーに言い換えを促す）
  const hasVisibleContent = parts.some((p) => p.functionCall || (p.text && !p.thought))
  if (json.stop_reason === 'refusal' && !hasVisibleContent) {
    return { candidates: [], promptFeedback: { blockReason: '安全システムによる拒否' } }
  }
  if (parts.length === 0) {
    return { candidates: [] }
  }

  return {
    candidates: [
      {
        content: { role: 'model', parts },
        ...(json.stop_reason === 'max_tokens' ? { finishReason: 'MAX_TOKENS' } : {}),
      },
    ],
  }
}

function extractErrorMessage(body: unknown): string | undefined {
  if (body && typeof body === 'object' && 'error' in body) {
    const error = (body as { error?: { message?: string } }).error
    return error?.message
  }
  return undefined
}

export async function generateAnthropic(request: GenerateRequest): Promise<GenerateResponse> {
  const { apiKey, model, contents, systemInstruction, functionDeclarations } = request

  const json = await postJsonWithRetry({
    provider: 'anthropic',
    url: API_URL,
    headers: {
      'x-api-key': apiKey,
      'anthropic-version': ANTHROPIC_VERSION,
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: {
      model: model.modelId,
      max_tokens: MAX_OUTPUT_TOKENS,
      system: systemInstruction,
      messages: toAnthropicMessages(contents),
      tools: functionDeclarations.map((decl) => ({
        name: decl.name,
        description: decl.description,
        input_schema: decl.parameters,
      })),
      ...thinkingParams(model.thinkingConfig),
    },
    retryableStatuses: new Set([429, 500, 503, 529]),
    extractErrorMessage,
    extractRetryDelayMs: (_json, response) => retryAfterMs(response),
    connectErrorMessage: 'Claude APIに接続できませんでした。ネットワークを確認してください。',
  })

  return toGenerateResponse(json as AnthropicResponse)
}
